import { useEffect, useRef, useCallback } from 'react'
import { useEditorStore } from '../store/editor.store'
import { sitesApi, projectsApi } from '../utils/api'

const DEBOUNCE_MS = 1500
const RETRY_DELAY_MS = 5000

export function useAutoSave() {
  const { page, pages, settings, globalHeader, globalFooter, setSaveStatus, loadedPageIds, replacePageId } = useEditorStore()
  const siteIdRef = useRef<string | null>(localStorage.getItem('bst-site-id'))
  const projectIdRef = useRef<string | null>(localStorage.getItem('bst-project-id'))
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const savingRef = useRef(false)
  // If a save is skipped because one is already in flight, retry immediately after completion
  const pendingSaveRef = useRef(false)
  // Retry timer for network errors — cancelled whenever a new save succeeds
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Always-current reference so retry closures call the latest save (with fresh state)
  const saveRef = useRef<() => Promise<void>>(() => Promise.resolve())

  const save = useCallback(async () => {
    if (savingRef.current) {
      pendingSaveRef.current = true
      return
    }
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }
    savingRef.current = true
    pendingSaveRef.current = false
    setSaveStatus('saving')
    try {
      const projectId = projectIdRef.current

      if (projectId) {
        // ── Project Engine path (new projects created via POST /api/projects/website) ──
        let saveError: string | null = null
        const siteId = siteIdRef.current
        if (siteId) {
          try { await sitesApi.update(siteId, {
            name: settings.name,
            favicon: settings.favicon,
            gaId: settings.gaId,
            ymId: settings.ymId,
            fbPixelId: settings.fbPixelId,
            vkPixelId: settings.vkPixelId,
            gtmId: settings.gtmId,
            themeConfig: settings.themeConfig || undefined,
            customHeadHtml: settings.customHeadHtml,
            customBodyHtml: settings.customBodyHtml,
            formWebhookUrl: settings.formWebhookUrl,
            formEmailTo: settings.formEmailTo,
            customDomain: settings.customDomain,
            globalHeader: globalHeader ? JSON.stringify(globalHeader) : '',
            globalFooter: globalFooter ? JSON.stringify(globalFooter) : '',
            cookieConsent: settings.cookieConsent,
            cookieConsentText: settings.cookieConsentText,
            mailchimpApiKey: settings.mailchimpApiKey,
            mailchimpListId: settings.mailchimpListId,
            unisenderApiKey: settings.unisenderApiKey,
            unisenderListId: settings.unisenderListId,
            sendpulseClientId: settings.sendpulseClientId,
            sendpulseClientSecret: settings.sendpulseClientSecret,
            sendpulseListId: settings.sendpulseListId,
            bitrix24WebhookUrl: settings.bitrix24WebhookUrl,
            amoCrmDomain: settings.amoCrmDomain,
            amoCrmToken: settings.amoCrmToken,
            formTelegramChatId: settings.formTelegramChatId,
            whatsappNumber: settings.whatsappNumber,
            telegramUsername: settings.telegramUsername,
            vkGroupId: settings.vkGroupId,
            callbackPhone: settings.callbackPhone,
            jivoChatId: settings.jivoChatId,
            crispWebsiteId: settings.crispWebsiteId,
            tawkPropertyId: settings.tawkPropertyId,
            tawkWidgetId: settings.tawkWidgetId,
            recaptchaSiteKey: settings.recaptchaSiteKey,
            recaptchaSecretKey: settings.recaptchaSecretKey,
            socialProofEnabled: settings.socialProofEnabled,
            socialProofMessages: settings.socialProofMessages,
            backToTop: settings.backToTop,
            scrollProgress: settings.scrollProgress,
            scrollProgressColor: settings.scrollProgressColor,
            pagePreloader: settings.pagePreloader,
            customCursor: settings.customCursor,
            customCursorColor: settings.customCursorColor,
            pageTransition: settings.pageTransition || undefined,
            siteLanguage: settings.siteLanguage,
            additionalLanguages: settings.additionalLanguages,
            pwaEnabled: settings.pwaEnabled,
            pwaName: settings.pwaName,
            pwaShortName: settings.pwaShortName,
            pwaThemeColor: settings.pwaThemeColor,
            pwaBackgroundColor: settings.pwaBackgroundColor,
            clarityProjectId: settings.clarityProjectId,
            hotjarSiteId: settings.hotjarSiteId,
            plausibleDomain: settings.plausibleDomain,
            matomoUrl: settings.matomoUrl,
            matomoSiteId: settings.matomoSiteId,
            googleVerification: settings.googleVerification,
            yandexVerification: settings.yandexVerification,
            bingVerification: settings.bingVerification,
            publiclyListed: settings.publiclyListed,
            publicListingTags: settings.publicListingTags,
          }) } catch { saveError = 'Не удалось сохранить настройки' }
        }

        // Pages go through Project Engine
        for (const p of pages) {
          if (!loadedPageIds.includes(p.id)) continue
          const pageDto = {
            title: p.title,
            slug: p.slug || '/',
            isHome: p.slug === '/' || p.slug === '',
            seoTitle: p.seoTitle,
            seoDesc: p.seoDesc,
            ogImage: p.ogImage,
            structuredData: p.structuredData || undefined,
            pageCss: p.pageCss || undefined,
            pageJs: p.pageJs || undefined,
            canonicalUrl: p.canonicalUrl || undefined,
            robotsIndex: p.robotsIndex,
            content: { sections: p.sections },
            ...(p.password !== undefined && { password: p.password }),
            ...(p.cmsTemplateFor !== undefined && { cmsTemplateFor: p.cmsTemplateFor }),
          }
          if (p.id.startsWith('bst-')) {
            // Local page — create on server, then swap the local ID for the server ID
            try {
              const created = await projectsApi.createPage(projectId, pageDto)
              replacePageId(p.id, created.id)
            } catch {
              // Will retry on next auto-save cycle
              saveError = 'Не удалось создать страницу'
            }
          } else {
            try {
              await projectsApi.updatePage(projectId, p.id, pageDto)
            } catch {
              saveError = 'Не удалось сохранить страницу'
            }
          }
        }
        setSaveStatus(saveError ? 'error' : 'saved', saveError)
        return
      }

      // ── Legacy path (old sites without a project ID) ─────────────────────────
      let siteId = siteIdRef.current

      // First save: create the site
      if (!siteId) {
        const slug = settings.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 40) || `site-${Date.now().toString(36)}`
        try {
          const site = await sitesApi.create(settings.name || 'Мой сайт', slug)
          siteId = site.id
          siteIdRef.current = siteId
          localStorage.setItem('bst-site-id', siteId)
          localStorage.setItem('bst-site-slug', slug)
        } catch (e) {
          setSaveStatus('error', e instanceof Error ? e.message : 'Не удалось создать сайт')
          return
        }
      }

      // Update site settings
      let saveError: string | null = null
      try {
        await sitesApi.update(siteId, {
          name: settings.name,
          favicon: settings.favicon,
          gaId: settings.gaId,
          ymId: settings.ymId,
          fbPixelId: settings.fbPixelId,
          vkPixelId: settings.vkPixelId,
          gtmId: settings.gtmId,
          themeConfig: settings.themeConfig || undefined,
          customHeadHtml: settings.customHeadHtml,
          customBodyHtml: settings.customBodyHtml,
          formWebhookUrl: settings.formWebhookUrl,
          formEmailTo: settings.formEmailTo,
          customDomain: settings.customDomain,
          globalHeader: globalHeader ? JSON.stringify(globalHeader) : '',
          globalFooter: globalFooter ? JSON.stringify(globalFooter) : '',
          cookieConsent: settings.cookieConsent,
          cookieConsentText: settings.cookieConsentText,
          mailchimpApiKey: settings.mailchimpApiKey,
          mailchimpListId: settings.mailchimpListId,
          unisenderApiKey: settings.unisenderApiKey,
          unisenderListId: settings.unisenderListId,
          sendpulseClientId: settings.sendpulseClientId,
          sendpulseClientSecret: settings.sendpulseClientSecret,
          sendpulseListId: settings.sendpulseListId,
          bitrix24WebhookUrl: settings.bitrix24WebhookUrl,
          amoCrmDomain: settings.amoCrmDomain,
          amoCrmToken: settings.amoCrmToken,
          formTelegramChatId: settings.formTelegramChatId,
          whatsappNumber: settings.whatsappNumber,
          telegramUsername: settings.telegramUsername,
          vkGroupId: settings.vkGroupId,
          callbackPhone: settings.callbackPhone,
          jivoChatId: settings.jivoChatId,
          crispWebsiteId: settings.crispWebsiteId,
          tawkPropertyId: settings.tawkPropertyId,
          tawkWidgetId: settings.tawkWidgetId,
          recaptchaSiteKey: settings.recaptchaSiteKey,
          recaptchaSecretKey: settings.recaptchaSecretKey,
          socialProofEnabled: settings.socialProofEnabled,
          socialProofMessages: settings.socialProofMessages,
          backToTop: settings.backToTop,
          scrollProgress: settings.scrollProgress,
          scrollProgressColor: settings.scrollProgressColor,
          pagePreloader: settings.pagePreloader,
          customCursor: settings.customCursor,
          customCursorColor: settings.customCursorColor,
          pageTransition: settings.pageTransition || undefined,
          siteLanguage: settings.siteLanguage,
          additionalLanguages: settings.additionalLanguages,
          pwaEnabled: settings.pwaEnabled,
          pwaName: settings.pwaName,
          pwaShortName: settings.pwaShortName,
          pwaThemeColor: settings.pwaThemeColor,
          pwaBackgroundColor: settings.pwaBackgroundColor,
          clarityProjectId: settings.clarityProjectId,
          hotjarSiteId: settings.hotjarSiteId,
          plausibleDomain: settings.plausibleDomain,
          matomoUrl: settings.matomoUrl,
          matomoSiteId: settings.matomoSiteId,
          googleVerification: settings.googleVerification,
          yandexVerification: settings.yandexVerification,
          bingVerification: settings.bingVerification,
          publiclyListed: settings.publiclyListed,
          publicListingTags: settings.publicListingTags,
        })
      } catch { saveError = 'Не удалось сохранить настройки' }

      // Save all pages — skip pages whose content hasn't been loaded yet
      for (const p of pages) {
        if (!loadedPageIds.includes(p.id)) continue
        try {
          await sitesApi.upsertPage(siteId, {
            title: p.title,
            slug: p.slug || '/',
            isHome: p.slug === '/' || p.slug === '',
            seoTitle: p.seoTitle,
            seoDesc: p.seoDesc,
            ogImage: p.ogImage,
            structuredData: p.structuredData || undefined,
            pageCss: p.pageCss || undefined,
            pageJs: p.pageJs || undefined,
            canonicalUrl: p.canonicalUrl || undefined,
            robotsIndex: p.robotsIndex,
            content: { sections: p.sections },
            ...(p.password !== undefined && { password: p.password }),
            ...(p.cmsTemplateFor !== undefined && { cmsTemplateFor: p.cmsTemplateFor }),
          })
        } catch {
          saveError = 'Не удалось сохранить страницу'
        }
      }
      setSaveStatus(saveError ? 'error' : 'saved', saveError)
    } catch (e) {
      setSaveStatus('error', e instanceof Error ? e.message : 'Ошибка сохранения')
    } finally {
      savingRef.current = false
      if (pendingSaveRef.current) {
        // A change arrived while we were saving — retry immediately
        pendingSaveRef.current = false
        void save()
      } else if (useEditorStore.getState().saveStatus === 'error') {
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null
          if (useEditorStore.getState().saveStatus === 'error') {
            void saveRef.current()
          }
        }, RETRY_DELAY_MS)
      }
    }
  }, [page, pages, settings, globalHeader, globalFooter, setSaveStatus, loadedPageIds, replacePageId])

  useEffect(() => { saveRef.current = save }, [save])

  useEffect(() => {
    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    // Mark dirty immediately so the UI reflects unsaved state during the debounce window
    setSaveStatus('dirty')
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(save, DEBOUNCE_MS)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [save])

  return { siteId: siteIdRef.current }
}
